import { JSX, Suspense, useCallback, useMemo, useState } from "react";

import { type SelectChangeEvent, Paper, Box, Grid, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useHistoryChartQuery } from "src/api/locationChartQueries.api";
import CardHeader from "src/components/CardHeader/CardHeader";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import WeatherInfluenceOnFootfallChart from "src/components/Dashboard/WeatherInfluenceOnFootfall/WeatherInfluenceOnFootfallChart";
import DownloadCSV from "src/components/DownloadCSV/DownloadCSV";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import MetricHeader from "src/components/MetricHeader/MetricHeader";
import MetricLeftBlock from "src/components/MetricHeader/MetricLeftBlock";
import ComponentId from "src/constants/componentId.enum";
import FrameTypeLastDays from "src/constants/frameTypeLastDays.enum";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import sharedStyles from "src/constants/styles";
import { mapSiteWeatherEventsInfluence } from "src/lib/chart.helper";
import { buildChartData } from "src/lib/date.helper";
import { formatNum, getAvgTemperature, getCurrentTotalCount, getDifference } from "src/lib/number.helpers";
import { MeasuresItemizedItemDto } from "src/types/locationChartDto";

import Dropdown from "./Dropdown/Dropdown";
import { type DashboardComponentParams } from "../interfaces/DashboardComponentParams";
import ExpectedWeatherInfo from "./Infos/ExpectedWeatherInfo/ExpectedWeatherInfo";
import NextEventInfo from "./Infos/NextEventInfo/NextEventInfo";

const getStyles = () => ({
  chartContainer: { height: 277 },
  fullWidth: { xs: 12 },
});

function WeatherInfluenceOnFootfallComponent({ selectedLocation }: DashboardComponentParams): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const [daysFrame, setDaysFrame] = useState(FrameTypeLastDays.LastSevenDays);

  const dayCount = useMemo(() => (daysFrame === FrameTypeLastDays.LastSevenDays ? 7 : 30), [daysFrame]);

  const { data: rawMeasuresData } = useHistoryChartQuery(
    selectedLocation.Id,
    IntervalType.Day,
    null,
    MeasureType.Count,
    dayCount,
    true,
    true
  );

  const measuresData = useMemo(() => {
    if (!rawMeasuresData) return null;
    const chartData = buildChartData(rawMeasuresData, "itemized", IntervalTab.Month) as MeasuresItemizedItemDto[];

    return {
      ...rawMeasuresData,
      data: chartData,
    };
  }, [rawMeasuresData]);

  const weatherData = useMemo(() => {
    const dataList = measuresData?.data || [];
    return {
      difference: getDifference(measuresData),
      totalCount: dataList.length ? getCurrentTotalCount(dataList) : 0,
      avgTemp: dataList.length ? getAvgTemperature(dataList) : 0,
      isWeatherAvailable: dataList.some(
        (item) => item.status !== "NA" && item.status !== "default" && item.temperature !== "" && item.temperature !== "NA"
      ),
    };
  }, [measuresData]);

  const dateRangeLabel = useMemo(() => {
    if (!measuresData?.currentDateRange) return "";

    const { start, end } = measuresData.currentDateRange;
    const startDate = dayjs(start);
    const endDate = dayjs(end).subtract(1, "day");

    const format = (d: dayjs.Dayjs) => `${t(d.format("MMM"))}, ${d.format("D YYYY")}`;
    return `${format(startDate)} - ${format(endDate)}`;
  }, [measuresData, t]);

  const handleDropdownChange = useCallback((event: SelectChangeEvent): void => {
    setDaysFrame(Number(event.target.value));
  }, []);

  const dataRows = useMemo(
    () => (measuresData ? mapSiteWeatherEventsInfluence(measuresData, selectedLocation.LocalName) : []),
    [measuresData, selectedLocation.LocalName]
  );

  return (
    <Paper sx={sharedStyles(theme).cardContainer}>
      <CardHeader header={t("Weather and events influence")} dateTime={dateRangeLabel} />

      <MetricHeader
        leftContent={
          <MetricLeftBlock
            variant='weather'
            totalCount={formatNum(weatherData.totalCount)}
            avgTemp={weatherData.avgTemp}
            diff={weatherData.difference}
            tempLabel={t("Avg Temperature")}
          />
        }
        rightContent={
          <>
            <Dropdown value={daysFrame} onChange={handleDropdownChange} />
            <DownloadCSV componentId={ComponentId.WeatherEventsInfluence} locationName={selectedLocation.LocalName} rows={dataRows} />
          </>
        }
      />

      <Box sx={getStyles().chartContainer}>
        <Suspense fallback={<ChartSkeleton />}>
          {measuresData ? <WeatherInfluenceOnFootfallChart chartDto={measuresData} /> : <ChartSkeleton />}
        </Suspense>
      </Box>

      <Grid container spacing={1}>
        <Grid size={getStyles().fullWidth}>
          <ExpectedWeatherInfo selectedLocation={selectedLocation} isWeatherAvailable={weatherData.isWeatherAvailable} />
        </Grid>
        <Grid size={getStyles().fullWidth}>
          <NextEventInfo selectedLocation={selectedLocation} />
        </Grid>
      </Grid>
    </Paper>
  );
}
export default WeatherInfluenceOnFootfallComponent;
